import React, { useState, useEffect } from "react";
import { Table, Input, Card, CardBody, CardTitle } from "reactstrap";
import { formatPriceWithDollar, toNumber } from "utils/currency";
import { useGetInHouseAprs, useUpdateInHouseAprs } from "api/in-house-aprs/service";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

const AprTab = ({ activeColorStyle, isTheme2 = false }) => {
    const inputStyle = isTheme2
        ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
        : undefined;
    const limitInputStyle = isTheme2
        ? { maxWidth: 150, fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
        : { maxWidth: 150 };
    const actionBtnStyle = isTheme2
        ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR, padding: "6px 12px", color: "white", border: "none" }
        : { fontSize: "12px", padding: "6px 12px", color: "white", border: "none" };
    const cardTitleStyle = isTheme2
        ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
        : { fontSize: 18 };
    const { data: aprData, isLoading } = useGetInHouseAprs();
    const updateAprMutation = useUpdateInHouseAprs();
    const [recordId, setRecordId] = useState(null);

    const [ranges, setRanges] = useState([
        { id: 1, limit: 20000, apr: 21.0 },
        { id: 2, limit: 25000, apr: 20.0 },
        { id: 3, limit: 30000, apr: 19.0 },
        { id: 4, limit: null, apr: 18.0 },
    ]);

    useEffect(() => {
        if (aprData?.results?.[0]?.apr) {
            setRecordId(aprData.results[0].id);
            const aprArray = aprData.results[0].apr;

            const apiRanges = aprArray.map((item, index) => ({
                id: index + 1,
                limit: (item.max === null || item.max === undefined) ? null : parseFloat(item.max),
                apr: parseFloat(item.apr)
            }));

            setRanges(apiRanges);
        }
    }, [aprData]);

    const handleLimitChange = (index, value) => {
        const val = toNumber(value);
        const newRanges = [...ranges];
        newRanges[index].limit = val;
        setRanges(newRanges);
    };

    const handleAprChange = (index, value) => {
        const newRanges = [...ranges];
        newRanges[index].apr = value;
        setRanges(newRanges);
    };

    const addRow = () => {
        const newRanges = [...ranges];
        const lastDefinedLimitRowIndex = newRanges.length - 2;
        const lastLimit = lastDefinedLimitRowIndex >= 0 ? newRanges[lastDefinedLimitRowIndex].limit : 0;
        const newLimit = lastLimit + 5000;

        newRanges.splice(newRanges.length - 1, 0, {
            id: Date.now(),
            limit: newLimit,
            apr: 0.0
        });

        setRanges(newRanges);
    };

    const removeRow = (index) => {
        const newRanges = ranges.filter((_, i) => i !== index);
        if (newRanges.length === 0) {
            setRanges([{ id: Date.now(), limit: null, apr: 0 }]);
            return;
        }
        setRanges(newRanges);
    };

    const getLabel = (index) => {
        const current = ranges[index];
        const prev = index > 0 ? ranges[index - 1] : null;
        const prevLimit = prev ? prev.limit : 0;

        if (current.limit === null) {
            return `${formatPriceWithDollar(prevLimit)} and above`;
        }
        if (index === 0) {
            return `Under ${formatPriceWithDollar(current.limit)}`;
        }
        return `${formatPriceWithDollar(prevLimit)} – ${formatPriceWithDollar(current.limit - 1)}`;
    };

    const handleSave = () => {
        const aprArray = ranges.map((range, index) => ({
            min: index === 0 ? "0" : String(ranges[index - 1].limit),
            max: range.limit === null ? null : String(range.limit),
            apr: String(range.apr)
        }));

        const payload = {
            apr: aprArray
        };

        updateAprMutation.mutate({
            id: recordId,
            data: payload
        });
    };

    if (isLoading) {
        return (
            <Card style={{ overflow: "hidden" }}>
                <CardBody>
                    <div className="text-center p-5">Loading APR settings...</div>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card style={{ overflow: "hidden" }}>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <CardTitle className='mb-0 font-weight-bold' style={cardTitleStyle}>APR</CardTitle>
                    <div className="d-flex" style={{ gap: 8 }}>
                        <button
                            style={{
                                ...activeColorStyle,
                                ...actionBtnStyle,
                            }}
                            onClick={addRow}
                        >
                            + Add Range
                        </button>
                        <button
                            style={{
                                ...activeColorStyle,
                                ...actionBtnStyle,
                            }}
                            onClick={handleSave}
                            disabled={updateAprMutation.isLoading}
                        >
                            {updateAprMutation.isLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
                <Table bordered responsive>
                    <thead>
                        <tr>
                            <th style={{ backgroundColor: '#27293d', color: 'white' }}>Vehicle Price Range</th>
                            <th style={{ backgroundColor: '#27293d', color: 'white' }}>APR</th>
                            <th style={{ backgroundColor: '#27293d', color: 'white', width: 50 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {ranges.map((row, index) => {
                            const prevLimit = index > 0 ? ranges[index - 1].limit : 0;
                            const isLast = row.limit === null;
                            const isInvalid = !isLast && row.limit <= prevLimit;

                            return (
                                <tr key={row.id}>
                                    <td className="align-middle">
                                        {!isLast ? (
                                            <div className="d-flex align-items-center">
                                                <span className="mr-2" style={{ minWidth: 60 }}>
                                                    {index === 0 ? "Under" : `${formatPriceWithDollar(prevLimit)} – `}
                                                </span>
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    style={{
                                                        ...limitInputStyle,
                                                        borderColor: isInvalid ? "red" : undefined,
                                                    }}
                                                    value={row.limit}
                                                    onChange={(e) => handleLimitChange(index, e.target.value)}
                                                />
                                            </div>
                                        ) : (
                                            <span>{getLabel(index)}</span>
                                        )}
                                    </td>
                                    <td className="align-middle" style={{ width: 150 }}>
                                        <div className="d-flex align-items-center">
                                            <Input
                                                type="number"
                                                step="0.1"
                                                className="form-control"
                                                style={inputStyle}
                                                value={row.apr}
                                                onChange={(e) => handleAprChange(index, e.target.value)}
                                            />
                                            <span className="ml-2">%</span>
                                        </div>
                                    </td>
                                    <td className="align-middle text-center">
                                        {!isLast && (
                                            <button
                                                onClick={() => removeRow(index)}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    fontSize: '20px',
                                                    cursor: 'pointer',
                                                    color: '#999',
                                                    padding: '0 8px'
                                                }}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </CardBody>
        </Card>
    );
};

export default AprTab;
